import React, { useState } from 'react';
import { User, AtSign, Mail, Lock, Eye, EyeOff } from 'lucide-react';

const AccountSetup = ({ data, onChange, onContinue }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showTerms, setShowTerms]       = useState(false);
  const [showPrivacy, setShowPrivacy]   = useState(false);

  // ── Validation (mirrors iOS AccountData) ────────────────────────────────
  const isEmailValid    = /^[A-Z0-9a-z._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(data.email);
  const isUsernameValid = /^[a-zA-Z0-9_]{3,20}$/.test(data.username);
  const isPasswordValid = data.password.length >= 8;
  const firstNameOk     = data.firstName.trim().length > 0;
  const legalOk         = data.acceptedTerms && data.acceptedPrivacy;

  const canProceed = firstNameOk && isUsernameValid && isEmailValid && isPasswordValid && legalOk;

  const set = (key) => (e) => onChange({ ...data, [key]: e.target.value });
  const toggle = (key) => () => onChange({ ...data, [key]: !data[key] });

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2D5F4D]/8 via-[#8B6F47]/5 to-[#F5F3EF]">
      <div className="max-w-lg mx-auto px-lg py-xxxl">

        {/* Header */}
        <div className="text-center mb-xxxl">
          <h1 className="text-[32px] font-serif text-[#2D5F4D] mb-sm" style={{ fontFamily: 'Georgia, serif' }}>
            Create Account
          </h1>
          <p className="text-callout text-[#8B7355]">Let's get you set up</p>
        </div>

        <div className="space-y-md">

          {/* Name row */}
          <div className="flex gap-md">
            <InputField
              placeholder="First Name"
              icon={<User size={18} />}
              value={data.firstName}
              onChange={set('firstName')}
              autoComplete="given-name"
            />
            <InputField
              placeholder="Last Name"
              icon={<User size={18} />}
              value={data.lastName}
              onChange={set('lastName')}
              autoComplete="family-name"
            />
          </div>

          {/* Username */}
          <InputField
            placeholder="Username"
            icon={<AtSign size={18} />}
            value={data.username}
            onChange={set('username')}
            autoComplete="username"
            autoCapitalize="none"
          />
          {data.username.length > 0 && (
            <p className={`text-caption px-xs -mt-xs ${isUsernameValid ? 'text-[#8B7355]' : 'text-red-500'}`}>
              {isUsernameValid
                ? `Your handle will be @${data.username}`
                : '3–20 characters, letters, numbers, underscores only'}
            </p>
          )}

          {/* Email */}
          <InputField
            placeholder="Email"
            icon={<Mail size={18} />}
            value={data.email}
            onChange={set('email')}
            type="email"
            autoComplete="email"
            autoCapitalize="none"
          />
          {data.email.length > 0 && !isEmailValid && (
            <p className="text-caption text-red-500 px-xs -mt-xs">
              Please enter a valid email address
            </p>
          )}

          {/* Password */}
          <div className="relative">
            <InputField
              placeholder="Password (8+ characters)"
              icon={<Lock size={18} />}
              value={data.password}
              onChange={set('password')}
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(p => !p)}
              className="absolute right-md top-1/2 -translate-y-1/2 text-[#2D5F4D]/40 hover:text-[#2D5F4D] transition-colors"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          {/* Legal */}
          <div className="pt-md space-y-sm">
            <LegalToggle
              text="I agree to the"
              linkText="Terms of Service"
              checked={data.acceptedTerms}
              onToggle={toggle('acceptedTerms')}
              onLink={() => setShowTerms(true)}
            />
            <LegalToggle
              text="I agree to the"
              linkText="Privacy Policy"
              checked={data.acceptedPrivacy}
              onToggle={toggle('acceptedPrivacy')}
              onLink={() => setShowPrivacy(true)}
            />
          </div>

          {/* Continue */}
          <button
            onClick={onContinue}
            disabled={!canProceed}
            className="w-full py-lg rounded-full bg-[#2D5F4D] text-white font-semibold text-callout hover:bg-[#2D5F4D]/90 disabled:opacity-40 disabled:cursor-not-allowed transition-all mt-md"
          >
            Continue
          </button>
        </div>
      </div>

      {/* Terms modal */}
      {showTerms && (
        <LegalModal title="Terms of Service" onClose={() => setShowTerms(false)}>
          <p className="text-callout text-[#4a4a4a] leading-relaxed">
            By using Nutrius, you agree to these terms. Nutrius provides a platform for tracking
            dietary preferences and healthy living. You are responsible for maintaining the security
            of your account. We reserve the right to terminate accounts that violate our community
            guidelines. All content you submit remains your property, but you grant Nutrius a license
            to display it within the app. We are not responsible for dietary advice provided through
            the platform — always consult a healthcare professional for medical decisions.
          </p>
        </LegalModal>
      )}

      {/* Privacy modal */}
      {showPrivacy && (
        <LegalModal title="Privacy Policy" onClose={() => setShowPrivacy(false)}>
          <p className="text-callout text-[#4a4a4a] leading-relaxed">
            Nutrius collects information you provide (name, email, dietary preferences) and usage
            data to improve the app. We do not sell your personal information to third parties.
            Your dietary data and care profiles are stored securely in Firebase. You can delete your
            account and all associated data at any time from the Profile settings. We use analytics
            to improve the app experience.
          </p>
        </LegalModal>
      )}
    </div>
  );
};

// ── Shared sub-components ────────────────────────────────────────────────────

const InputField = ({ placeholder, icon, value, onChange, type = 'text', autoComplete, autoCapitalize }) => (
  <div className="flex items-center gap-sm px-md py-md bg-white/80 rounded-2xl border border-[#2D5F4D]/10 focus-within:border-[#2D5F4D]/30 transition-all w-full">
    <span className="text-[#2D5F4D]/40 flex-shrink-0">{icon}</span>
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      autoComplete={autoComplete}
      autoCapitalize={autoCapitalize}
      className="flex-1 bg-transparent text-body text-[#2D5F4D] placeholder:text-[#2D5F4D]/30 outline-none min-w-0"
    />
  </div>
);

const LegalToggle = ({ text, linkText, checked, onToggle, onLink }) => (
  <div className="flex items-center gap-md">
    <button onClick={onToggle} className="flex-shrink-0">
      <div className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-all ${
        checked ? 'bg-[#2D5F4D] border-[#2D5F4D]' : 'border-[#2D5F4D]/30 bg-white'
      }`}>
        {checked && (
          <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
            <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </div>
    </button>
    <span className="text-callout text-[#8B7355]">
      {text}{' '}
      <button onClick={onLink} className="text-[#2D5F4D] underline font-semibold">
        {linkText}
      </button>
    </span>
  </div>
);

const LegalModal = ({ title, children, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-lg bg-black/40 backdrop-blur-sm">
    <div className="w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[80vh]">
      <div className="flex items-center justify-between p-lg border-b border-[#2D5F4D]/10">
        <h2 className="text-title-2 text-[#2D5F4D] font-serif" style={{ fontFamily: 'Georgia, serif' }}>
          {title}
        </h2>
        <button onClick={onClose} className="text-[#2D5F4D]/50 hover:text-[#2D5F4D] transition-colors font-semibold">
          Done
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-lg">{children}</div>
    </div>
  </div>
);

export default AccountSetup;